import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import * as moment from 'moment';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { selectedAddressModel } from 'src/app/core/models/selectedAddress.interface';

import { RestapiService } from '@services/restapi.service';
import { SessionService } from '@services/session.service';
import { TealiumUtagService } from '@services/utag.service';
import { UtilityService } from '@services/utility.service';
import { MatSelectModule } from '@angular/material/select';
import { GoogleAnalyticsService } from '@services/google-analytics.service';


@Component({
  selector: 'app-postcode-lookup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  templateUrl: './postcode-lookup.component.html',
  styleUrl: './postcode-lookup.component.scss'
})
export class PostcodeLookupComponent implements OnInit, OnChanges, OnDestroy {
  @Input('isHomePage') isHomePage: boolean = false;
  @Input('isIndividual') isIndividual: boolean = false;
  @Input('regex') regex: any;
  @Input('isReset') isReset: boolean = false;
  @Output('isProgress') isProgress: EventEmitter<boolean> = new EventEmitter();
  @Output('addedNewAddress') addNew: EventEmitter<any> = new EventEmitter();
  @Output('resetComplete') resetComplete: EventEmitter<boolean> = new EventEmitter();
  subscriptions: Subscription[] = [];
  isLoggedIn: boolean = false;
  id: string;
  emailId: string;
  postcode: string;
  partialPostcode: string;
  abVariant: string;
  ctmFlowVariant: string;

  tealiumData: any;
  googleAnalytics = inject(GoogleAnalyticsService);

  postcodeLookupFormgroup: FormGroup;
  isAddressLookUp: boolean = false;
  addressList: any[] = [];
  gbgVersion: string = '';
  isVisible: boolean = false;

  address: selectedAddressModel;

  errorMessage: string = '';

  constructor(
    private sessionService: SessionService,
    private utilityService: UtilityService,
    private tealium: TealiumUtagService,
    private router: Router,
    private restAPI: RestapiService,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute
  ) {
    this.emailId = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
    this.postcode = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode);
    this.partialPostcode = this.utilityService.partialPostcode(this.postcode);
    this.isLoggedIn = this.utilityService.isLoggedIn();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset form if isReset is true
    if (changes['isReset'] && changes['isReset'].currentValue) {
      this.address = null;
      this.postcodeLookupFormgroup.get('addressId').reset('');
      this.isVisible = false;
      this.resetComplete.emit(false);
    }
  }

  ngOnInit(): void {
    this.abVariant = this.utilityService.getCookie(Resources.ABtestingKeys.LANDING_PAGE_VARIANT_COOKIE) || 'A';
    this.ctmFlowVariant = this.utilityService.getCookie(Resources.ABtestingKeys.CTM_FLOW_VARIANT_COOKIE) || 'A';

    // Initialise form group
    this.postcodeLookupFormgroup = this.fb.group({
      postcode: [this.isHomePage ? '' : this.postcode, [Validators.required, Validators.pattern(this.regex?.postcode || Resources.Regex.Postcode)]],
      addressId: ['']
    });

    // Dynamicly add new form control
    if (this.isHomePage) {
      this.postcodeLookupFormgroup.get('addressId').setValidators([Validators.required]);
      this.postcodeLookupFormgroup.get('addressId').updateValueAndValidity();
    }

    // Initialise tealium data
    this.tealiumData = {
      // app variables
      app_name: environment.tealium_app_name,
      app_version: environment.tealium_app_version,
      app_platform: environment.tealium_app_platform,

      // page variables
      page_name: 'PostcodeEntry',
      page_type: 'RiskJourney',

      customer_partialpostcode: !!this.partialPostcode ? this.partialPostcode : undefined,
      // Base URL
      base_url: window.location.href,
      energycomparison_subscribed: this.emailId ? true : false,

      customer_id: !!this.emailId ? this.utilityService.encryptIntoSha256(this.emailId.toLowerCase().trim()) : undefined
    };

    // Trigger Tealium view event
    this.tealium.viewEvent(this.tealiumData);

    // Add any initialization logic here if needed
    this.id = this.tealiumData['app_name'] + '_' + this.tealiumData['page_name'];
  }

  tealiumDataSet(state: string): void {
    // On focus set tealium data
    if (state === 'focus') {
      this.tealiumData['event_label'] = 'PostcodeFieldEntry';
      this.tealiumData['event_category'] = `YourPostcode`;
      this.tealiumData['event_action'] = 'onEntry';
      // error message
    } else if (state === 'blur') {
      this.tealiumData['event_label'] = (this.postcodeLookupFormgroup.get('postcode').valid) ? 'PostcodeFieldExit' : 'InvalidPostcode';
      this.tealiumData['event_category'] = `YourPostcode`;
      this.tealiumData['event_action'] = 'onExit';
    }

    // Trigger Tealium event
    this.tealium.linkEvent(this.tealiumData);

    // error message
    // check if form is valid
    if (this.postcodeLookupFormgroup.valid) {
      this.errorMessage = '';
    } else {
      this.errorMessage = 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
    }
  }

  // Method to handle postcode lookup
  postcodelookUp(): void {
    this.isAddressLookUp = true;
    // check if form is valid
    if (this.postcodeLookupFormgroup.get('postcode').valid) {
      const triggerAPITime = new Date().getTime();
      const postcode = this.postcodeLookupFormgroup.value.postcode?.toUpperCase()?.trim();
      // Trigger Tealium event
      this.tealiumData['event_label'] = 'FindAddress';
      this.tealiumData['event_category'] = `YourPostcode`;
      this.tealiumData['event_action'] = 'onClick';
      this.tealiumData['customer_partialpostcode'] = this.utilityService.partialPostcode(postcode);

      // Trigger Tealium event
      this.tealium.linkEvent(this.tealiumData);

      // Trigger Google Analytics event
      this.googleAnalytics.track({ partial_postcode: this.utilityService.partialPostcode(postcode), abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'postcode_lookup');

      // Get address list
      this.subscriptions.push(
        this.restAPI.getAPI(`/v1/users/addressesWrapper?postcode=${postcode}`).subscribe({
          next: (data: any) => {
            this.addressList = data?.data;
            this.gbgVersion = data?.gbgVersion;
            this.isAddressLookUp = false;
            this.isVisible = true;

            // Store postcode in session storage
            this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, postcode);
          }, error: (err) => {
            console.log('ERROR: ', err);
            // Trigger Tealium event
            this.tealiumData['event_label'] = '_InvalidPostcode';
            this.tealiumData['customer_partialpostcode'] = undefined;
            this.tealium.linkEvent(this.tealiumData);

            // Trigger Google Analytics event
            this.googleAnalytics.track({ partial_postcode: this.utilityService.partialPostcode(postcode), abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'postcode_invalid');

            this.postcodeLookupFormgroup.get('postcode').setErrors({ invalid: true });
            this.postcodeLookupFormgroup.markAllAsTouched();

            // [TEAM] Error handling - Refactoring required
            if (err?.error?.errorCode === 'CTM_INTERNAL_CHECK_ISSUE' || err?.error?.status === 'CTM_INTERNAL_CHECK_ISSUE') {
              this.errorMessage = `In the last 24 hours we've noticed a large volume of searches from your IP address so we have temporarily limited access to our site.`;
            } else if (
              err?.error?.error && err?.error?.error[0]?.message === 'postCode is not defined' ||
              err?.error?.error && err?.error?.error[0]?.message.substring(0, 36) === 'Addresses not found for the postcode' ||
              err?.error?.error === 'Invalid Postcode'
            ) {
              this.errorMessage = 'It looks like something has gone wrong while looking up your address.<br> Please check your connection and try again in a few moments.';
            } else {
              // We’re sorry, our energy service doesn’t currently support your region.
              this.errorMessage = err?.error?.msg?.description || 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
            }

            // Error email trigger
            if (err?.error?.additionalAttributes?.emailErrorRequired === 'Y') {
              const responseTimeMS = (new Date().getTime() - triggerAPITime) / 1000;
              const subject = (err?.error?.errorCode === 'CTM_INTERNAL_CHECK_ISSUE' || err?.error?.status === 'CTM_INTERNAL_CHECK_ISSUE') ? environment.ENV + ' - CTM_INTERNAL_CHECK_ISSUE - Something went wrong, please try again later.' : environment.ENV + '- PostCode Api Failed';
              this.utilityService.sendAPIError(subject, { postcode }, { postcode: err }, responseTimeMS);
            }

            this.isAddressLookUp = false;
          }
        })
      );

      // this.isAddressLookUp = false;
    } else {
      // Trigger Tealium event
      this.tealiumData['event_label'] = '_InvalidPostcode';
      this.tealiumData['customer_partialpostcode'] = undefined;
      this.errorMessage = 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
      this.postcodeLookupFormgroup.markAllAsTouched();
      this.isAddressLookUp = false;
    }
  }

  // Selected address
  selectedAddress(address: any): void {
    // Notify to manage multiple address to show in progress
    if (this.isIndividual) {
      this.isProgress.emit(true);
    }
    // If coming from home page
    if (this.isHomePage) {
      address = this.addressList.filter((item) => item.addressId === address)[0];
    }

    // flat address mpan
    if (address?.mpan) {
      address['mpan'] = address?.mpan?.[0];
    }
    // flat address mprn
    if (address?.mprn) {
      address['mprn'] = address?.mprn?.[0];
    }

    // Trigger Tealium event
    this.tealiumData['event_label'] = 'AddressSelected';
    this.tealiumData['event_category'] = `YourPostcode`;
    this.tealiumData['event_action'] = 'onClick';
    this.tealiumData['customer_partialpostcode'] = this.utilityService.partialPostcode(this.postcode);

    this.tealium.linkEvent(this.tealiumData);

    // Trigger Google Analytics event
    this.googleAnalytics.track({
      partial_postcode: this.utilityService.partialPostcode(this.postcode),
      abVariant: this.abVariant,
      address: address?.addressId,
      ctmFlowVariant: this.ctmFlowVariant
    }, 'address_selected');

    // If MPAN is not present in address, then get meter number
    if (!address?.mpan) {
      this.getMeter(address);
    } else {
      this.checkBlockedAddress(address);
    }

    // If it is comming from home page
    if (this.isHomePage) {
      // Return new address details or navigate to dashboard
      this.addNewAddress(address);
    }
  }

  // Get meter number
  getMeter(address: any): void {
    const payload = {
      addressId: address?.addressId,
      gbgVersion: this.gbgVersion
    };

    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/meterWrapper', payload).subscribe({
        next: (data: any) => {
          // MPAN
          if (data?.mpan) {
            address['mpan'] = data?.mpan;
          }
          // MPRN
          if (data?.mprn) {
            address['mprn'] = data?.mprn;
          }

          // Not executed if it is coming from home page
          if (!this.isHomePage) {
            // Return new address details or navigate to dashboard
            this.addNewAddress(address);
          }
        }, error: (err) => {
          console.log('ERROR: ', err);
        }
      })
    );
  }

  // Check blocked address
  checkBlockedAddress(address: any): void {
    const cPath = '/v2/users/blocked-address-check?'
    const queryParams = {
      label: address?.Text,
      addressId: address?.addressId,
      fuelType: address?.fuelType,
      postcode: address?.postcode
    }
    let payload = {};
    // Add MPAN to payload
    if (address?.mpan) {
      payload['mpan'] = address?.mpan;
    }
    // Add MPRN to payload
    if (address?.mprn) {
      payload['mprn'] = address?.mprn;
    }

    this.subscriptions.push(
      this.restAPI.postAPI(cPath + new URLSearchParams(queryParams), payload).subscribe({
        next: (data: any) => {
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsAddressBlocked, data?.addressBlocked);
          this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.CurrentUsage);
          this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.NetzeroAPI);

          // Not executed if it is coming from home page
          if (!this.isHomePage) {
            // Return new address details or navigate to dashboard
            this.addNewAddress(address);
          }
        }, error: (err) => {
          console.log('ERROR: ', err);
        }
      })
    );
  }

  // Navigate to dashboard
  addNewAddress(address: any): void {
    const selectedAddress: selectedAddressModel = {
      addressId: address?.addressId,
      addressText: address?.Text,
      addressPostcode: address?.postcode,
      gbgVersion: this.gbgVersion,
      dnoId: address?.dnoId
    };

    // MPAN
    if (address?.mpan) {
      selectedAddress['mpan'] = address?.mpan;
    }

    // MPRN
    if (address?.mprn) {
      selectedAddress['mprn'] = address?.mprn;
    }

    // Store only if not coming from home page
    if (!this.isHomePage) {
      // Store selected address in session storage
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.SelectedAddress, selectedAddress);
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.DnoId, selectedAddress?.dnoId);
    }

    if (!this.isIndividual) {
      // Navigate to dashboard
      const queryParams = this.activatedRoute.snapshot.queryParams;
      this.router.navigate(['/information'], { queryParams: queryParams });
    } else {
      // Created address response
      const addressResponse = {
        addressId: selectedAddress?.addressId,
        addressLine: selectedAddress?.addressText,
        gbgVersion: selectedAddress?.gbgVersion,
        internalAddressId: selectedAddress?.addressId,
        dnoId: selectedAddress?.dnoId,
        postcode: selectedAddress?.addressPostcode,
        switchFlag: false,
        updatedAt: moment().format('YYYY-MM-DD HH:mm:ss')
      };
      // Notify to manage multiple address
      this.addNew.emit({ addNewAddress: addressResponse, selectedAddress: selectedAddress });
      this.address = selectedAddress;
    }

    // Removed EV details from session storage
    this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.EVDETAILS);
  }

  // Hide dropdown
  hide(): boolean {
    this.isVisible = false;
    return true;
  }

  ngOnDestroy(): void {
    // Cleanup if necessary
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
